import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { createEditageApi } from './api/editage-api';
import { AuthState, useAuthState } from './auth-state';
import { GetSsoToken } from './sso';
import { User } from './user';

type AuthContext = {
  authState: AuthState;
  isUserSignedUp: boolean;
  api: ReturnType<typeof createEditageApi>;
  listeners: {
    onUserCreated: (token: string) => void;
  };
  authPageConfiguration?: AuthPageConfiguration;
  trackEvent: (
    event: [category: string, action: string, label: string],
  ) => void;
  editage: {
    product: string;
    baseUrl: string;
    domain: string;
  };
  socialSignIn?: SocialSignInConfiguration;
};

const AuthContext = createContext<AuthContext>({
  authState: {
    name: 'logged out',
    invalidate: () => {},
    logIn: () => {},
    logOut: () => {},
    error: undefined,
    update: () => {},
  },
  trackEvent: () => {
    throw new Error('Auth context not proper configured');
  },
  editage: {
    product: '',
    baseUrl: '',
    domain: '',
  },
  listeners: {
    onUserCreated: () => {},
  },
  api: {
    signUp: async () => {
      throw new Error('Auth context not proper configured');
    },
    changePassword: async () => {
      throw new Error('Auth context not proper configured');
    },
  },
  isUserSignedUp: false,
});

export const AuthProvider = (
  props: PropsWithChildren<{
    trackEvent: (
      event: [category: string, action: string, label: string],
    ) => void;
    isUserSignedUp: boolean;
    authContextListener: AuthContextListener;
    authContextSetup: AuthContextSetup;
    editageConfiguration: {
      baseUrl: string;
      product: string;
      domain: string;
    };
    authPageConfiguration?: AuthPageConfiguration;
    socialSignIn?: SocialSignInConfiguration;
  }>,
) => {
  const editageApi = useMemo(() => {
    return createEditageApi({
      baseUrl: props.editageConfiguration.baseUrl,
      product: props.editageConfiguration.product,
      domain: props.editageConfiguration.domain,
    });
  }, [
    props.editageConfiguration.baseUrl,
    props.editageConfiguration.product,
    props.editageConfiguration.domain,
  ]);
  const authState = useAuthState({
    getSsoToken: ({ credentials }) =>
      props.authContextSetup.getSsoToken(credentials),
    getUser: props.authContextSetup.getUser,
    on: {
      gotUser: props.authContextListener.onGotUser,
      failedToGetUser: props.authContextListener.onFailedToGetUser,
      loggedIn: props.authContextListener.onLoggedIn,
      loggedOut: props.authContextListener.onLoggedOut,
    },
  });

  const value: AuthContext = {
    api: editageApi,
    isUserSignedUp: props.isUserSignedUp,
    authPageConfiguration: props.authPageConfiguration,
    authState,
    listeners: {
      onUserCreated: props.authContextListener.onUserCreated,
    },
    editage: {
      product: props.editageConfiguration.product,
      baseUrl: props.editageConfiguration.baseUrl,
      domain: props.editageConfiguration.domain,
    },
    trackEvent: props.trackEvent,
    socialSignIn: props.socialSignIn,
  };

  const shouldEnable3rdPartyGoogleAuthLibrary =
    value.socialSignIn?.enabled === 'true' &&
    value.socialSignIn?.useGoogleOAuth &&
    value.socialSignIn?.google?.clientId;

  return (
    <AuthContext.Provider value={value}>
      {shouldEnable3rdPartyGoogleAuthLibrary ? (
        <GoogleOAuthProvider
          clientId={String(props.socialSignIn?.google?.clientId ?? '')}
        >
          {props.children}
        </GoogleOAuthProvider>
      ) : (
        props.children
      )}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};
export interface Credentials {
  email: string;
  password: string;
}

export interface AuthPageConfiguration {
  url: {
    loginUrl: string;
    signUpUrl: string;
    forgotPasswordUrl: string;
  };
  linkWrapper: (
    href: string,
    component: ReactNode,
    emailValue?: string,
  ) => ReactElement;
  logo: ReactElement;
  groupUsers?: ReactElement;
}
export interface AuthContextSetup {
  getSsoToken: (credentials: Credentials) => ReturnType<GetSsoToken>;
  getUser: () => Promise<User>;
}

export interface AuthContextListener {
  onGotUser: (user: User) => void;
  onFailedToGetUser: () => void;
  onLoggedIn: () => void;
  onLoggedOut: () => void;
  onUserCreated: (token: string) => void;
}

export interface SocialSignInConfiguration {
  enabled: string | undefined;
  useGoogleOAuth: boolean;
  google?: {
    clientId: string | undefined;
  };
}
